import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AvaliacaoForm from './AvaliacaoForm';
import { useAuth } from '../context/AuthContext';
import './AvaliacaoPremio.css';

const CATEGORIA_INFO = {
  temas_livres: { titulo: 'Temas Livres', subtitulo: 'Avaliação da Comissão Julgadora', icone: '📋' },
  posteres: { titulo: 'Pôsteres', subtitulo: 'Avaliação da Comissão Julgadora — Totens', icone: '🖼️' },
  temas_correlatos: { titulo: 'Temas Correlatos', subtitulo: 'Avaliação da Comissão Julgadora', icone: '🎤' },
};

const obterAuditorio = (sessao) => {
  const tipo = sessao.tipo || '';
  const encontradoNoTipo = tipo.match(/Audit[oó]rio\s+(.+)$/i);
  if (encontradoNoTipo) return `Auditório ${encontradoNoTipo[1].trim()}`;

  const nome = sessao.nome || '';
  const encontradoNoNome = nome.match(/Audit[oó]rio\s+(.+?)(?:\s*[·-]\s*Sess[aã]o|\s*$)/i);
  return encontradoNoNome ? `Auditório ${encontradoNoNome[1].trim()}` : '';
};

const obterNumeroSessao = (sessao) => {
  const encontrado = (sessao.nome || '').match(/Sess[aã]o\s*(\d+)/i);
  return encontrado ? Number(encontrado[1]) : Number.MAX_SAFE_INTEGER;
};

function AvaliacaoPremio({ categoria = 'temas_livres' }) {
  const { user } = useAuth();
  const [sessoes, setSessoes] = useState([]);
  const [apresentacaoSelecionada, setApresentacaoSelecionada] = useState(null);
  const [avaliacoesFeitas, setAvaliacoesFeitas] = useState(new Set());
  const [successId, setSuccessId] = useState(null);
  const [errorId, setErrorId] = useState(null);
  const [diaSelecionado, setDiaSelecionado] = useState('');
  const [totemSelecionado, setTotemSelecionado] = useState(1);
  const info = CATEGORIA_INFO[categoria] || CATEGORIA_INFO.temas_livres;

  const fetchDados = useCallback(() => {
    Promise.all([
      axios.get('/api/sessoes/', { params: { categoria } }),
      axios.get('/api/minhas-avaliacoes/')
    ])
      .then(function(results) {
        setSessoes(results[0].data);
        const idsAvaliados = new Set(results[1].data.map(function(a) { return a.apresentacao_id; }));
        setAvaliacoesFeitas(idsAvaliados);
      })
      .catch(function(error) { console.error('Erro ao buscar dados!', error); });
  }, [categoria]);

  useEffect(() => {
    fetchDados();
    const interval = setInterval(fetchDados, 30000);
    return () => clearInterval(interval);
  }, [fetchDados]);

  const diasDisponiveis = React.useMemo(
    () => [...new Set(sessoes.map(s => s.dia))].sort(),
    [sessoes]
  );

  useEffect(() => {
    if (!diasDisponiveis.length) {
      setDiaSelecionado('');
      return;
    }
    setDiaSelecionado(atual => diasDisponiveis.includes(atual) ? atual : diasDisponiveis[0]);
  }, [diasDisponiveis, categoria]);

  const sessoesDoDiaBase = sessoes
    .filter(sessao => sessao.dia === diaSelecionado)
    .sort((a, b) => {
      if (categoria !== 'temas_correlatos') return 0;
      const diferencaNumero = obterNumeroSessao(a) - obterNumeroSessao(b);
      if (diferencaNumero !== 0) return diferencaNumero;
      return (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { numeric: true });
    });
  const totensDisponiveis = React.useMemo(() => {
    if (categoria !== 'posteres') return [];
    return [...new Set(
      sessoes
        .filter(sessao => sessao.dia === diaSelecionado)
        .flatMap(sessao => (sessao.apresentacoes || []).map(ap => ap.totem))
        .filter(Boolean)
    )].sort((a, b) => a - b);
  }, [sessoes, diaSelecionado, categoria]);

  useEffect(() => {
    if (categoria !== 'posteres' || !totensDisponiveis.length) return;
    setTotemSelecionado(atual => totensDisponiveis.includes(atual) ? atual : totensDisponiveis[0]);
  }, [totensDisponiveis, categoria]);

  const sessoesDoDia = categoria === 'posteres'
    ? sessoesDoDiaBase.filter(sessao => (sessao.apresentacoes || []).some(ap => ap.totem === totemSelecionado))
    : sessoesDoDiaBase;
  const formatarDiaAba = (dia) => {
    const data = new Date(`${dia}T12:00:00`);
    return {
      semana: data.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''),
      data: data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
    };
  };

  const handleAvaliacaoSucesso = (apresentacaoId) => {
    setAvaliacoesFeitas(prev => new Set(prev).add(apresentacaoId));
    setApresentacaoSelecionada(null);
    setSuccessId(apresentacaoId);
    setTimeout(() => setSuccessId(null), 5000);
  };

  const handleErroDuplicata = (apresentacaoId) => {
    setAvaliacoesFeitas(prev => new Set(prev).add(apresentacaoId));
    setApresentacaoSelecionada(null);
    setErrorId(apresentacaoId);
    setTimeout(() => setErrorId(null), 5000);
  };

  if (sessoes.length === 0) {
    return (
      <div className="premio-aval-container">
        <div className="premio-aval-header">
          <span className="premio-aval-icon">{info.icone}</span>
          <div>
            <span className="premio-aval-eyebrow">CBACV 2026 · Área do avaliador</span>
            <h1>{info.titulo}</h1>
            <p>{info.subtitulo}</p>
          </div>
        </div>
        <div className="premio-aval-vazio">
          <div className="premio-vazio-icon" aria-hidden="true">✓</div>
          <h2>Tudo certo por aqui</h2>
          <p>Nenhuma apresentação está disponível para avaliação neste momento.</p>
          <span>As novas apresentações aparecerão automaticamente quando a comissão liberar a avaliação.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="premio-aval-container">
      <div className="premio-aval-header">
        <span className="premio-aval-icon">{info.icone}</span>
        <div>
          <span className="premio-aval-eyebrow">CBACV 2026 · Área do avaliador</span>
          <h1>{info.titulo}</h1>
          <p>{info.subtitulo} — CBACV 2026</p>
        </div>
      </div>

      <div className="premio-dia-tabs" role="tablist" aria-label="Dias de apresentação">
        {diasDisponiveis.map(dia => {
          const rotulo = formatarDiaAba(dia);
          const quantidade = sessoes.filter(s => s.dia === dia).reduce((total, s) => total + (s.apresentacoes?.length || 0), 0);
          return (
            <button
              key={dia}
              type="button"
              role="tab"
              aria-selected={diaSelecionado === dia}
              className={diaSelecionado === dia ? 'is-active' : ''}
              onClick={() => setDiaSelecionado(dia)}
            >
              <span>{rotulo.semana}</span>
              <strong>{rotulo.data}</strong>
              <small>{quantidade} trabalho(s)</small>
            </button>
          );
        })}
      </div>

      {categoria === 'posteres' && totensDisponiveis.length > 0 && (
        <div className="premio-totem-tabs-wrap">
          <span className="premio-totem-tabs-label">Escolha o totem</span>
          <div className="premio-totem-tabs" role="tablist" aria-label="Totens disponíveis no dia selecionado">
            {totensDisponiveis.map(numero => {
              const quantidade = sessoesDoDiaBase.reduce(
                (total, sessao) => total + (sessao.apresentacoes || []).filter(ap => ap.totem === numero).length,
                0
              );
              return (
                <button
                  key={numero}
                  type="button"
                  role="tab"
                  aria-selected={totemSelecionado === numero}
                  className={`premio-totem-tab premio-totem-tab-${numero} ${totemSelecionado === numero ? 'is-active' : ''}`}
                  onClick={() => setTotemSelecionado(numero)}
                >
                  <span>▣</span>
                  <strong>Totem {numero}</strong>
                  <small>{quantidade} pôster(es)</small>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {sessoesDoDia.map(sessao => {
        const grupoTotem = categoria === 'posteres'
          ? (sessao.totens_avaliadores || []).find(item => item.totem === totemSelecionado)
          : null;
        const idsDesignados = categoria === 'posteres'
          ? (grupoTotem?.avaliadores || [])
          : (sessao.avaliadores || []);
        const usuarioDesignado = idsDesignados.some(id => Number(id) === Number(user?.id));

        return (
        <div key={sessao.id} className="premio-aval-card">
          <div className="premio-aval-sessao-info">
            <div className="premio-aval-sessao-identidade">
              {categoria === 'temas_correlatos' && obterAuditorio(sessao) && (
                <span className="premio-aval-auditorio">
                  <span aria-hidden="true">📍</span>
                  {obterAuditorio(sessao)}
                </span>
              )}
              <span className="premio-aval-nome">{sessao.nome}</span>
              {categoria === 'posteres' ? (
                <div className="premio-aval-avaliadores premio-aval-avaliadores-totens" aria-label="Avaliadores designados por totem">
                  {(() => {
                    return <span className="premio-aval-totem-equipe">
                      <strong>👥 Avaliadores do Totem {totemSelecionado}:</strong>{' '}
                      {grupoTotem?.avaliadores_detalhes?.length
                        ? grupoTotem.avaliadores_detalhes.map(a => `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username).join(', ')
                        : 'nenhum avaliador designado'}
                    </span>;
                  })()}
                </div>
              ) : (
                <div className="premio-aval-avaliadores" aria-label="Avaliadores designados para esta sessão">
                  <span className="premio-aval-avaliadores-label">👥 Avaliadores</span>
                  {(sessao.avaliadores_detalhes || []).length > 0 ? (
                    <span className="premio-aval-avaliadores-nomes">
                      {sessao.avaliadores_detalhes.map(a => `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username).join(' · ')}
                    </span>
                  ) : <span className="premio-aval-avaliadores-vazio">Nenhum avaliador designado</span>}
                </div>
              )}
            </div>
            <span className="premio-aval-data">
              📅 {new Date(sessao.dia).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}
              &nbsp;|&nbsp;
              🕐 {sessao.horario_inicio?.substring(0,5)} – {sessao.horario_fim?.substring(0,5)}
            </span>
          </div>

          {!usuarioDesignado && (
            <div className="premio-aval-leitura-alerta" role="status">
              👁 Você pode visualizar, mas não está designado para avaliar {categoria === 'posteres' ? `este totem nesta sessão` : 'esta sessão'}.
            </div>
          )}

          <ul className={`premio-aval-lista ${categoria === 'posteres' ? 'premio-lista-totens' : ''}`}>
            {[...sessao.apresentacoes]
              .filter(apresentacao => categoria !== 'posteres' || apresentacao.totem === totemSelecionado)
              .sort((a, b) => (a.horario_inicio || '').localeCompare(b.horario_inicio || '') || (a.totem || 99) - (b.totem || 99))
              .map(apresentacao => (
              <li key={apresentacao.id} className={`premio-aval-item ${categoria === 'posteres' ? 'premio-aval-item-totem' : ''}`}>
                {categoria === 'posteres' && (
                  <div className={`premio-totem-identificador premio-totem-${apresentacao.totem || 'sem'}`}>
                    <span className="premio-totem-tv">▣</span>
                    <span>Totem</span>
                    <strong>{apresentacao.totem || '—'}</strong>
                  </div>
                )}
                <div className="premio-aval-item-conteudo">
                <div className="ap-horario-badge">
                  🕐 {apresentacao.horario_inicio?.substring(0,5)} – {apresentacao.horario_fim?.substring(0,5)}
                </div>
                <strong className="premio-aval-titulo">{apresentacao.titulo}</strong>
                <p className="premio-aval-autor">Apresentador(a): {apresentacao.apresentador_principal}</p>
                {apresentacao.lista_autores?.trim() && (
                  <p className="premio-aval-autores">Autores: {apresentacao.lista_autores}</p>
                )}
                <div className="action-area">
                  {!usuarioDesignado ? (
                    <button className="btn-sem-acesso" disabled>👁 Somente leitura</button>
                  ) : avaliacoesFeitas.has(apresentacao.id) ? (
                    <button className="btn-avaliado" disabled>✔️ Avaliado</button>
                  ) : !apresentacao.avaliacao_aberta ? (
                    <button className="btn-avaliado" disabled>⛔ Não Avaliado</button>
                  ) : (
                    <button className="btn-avaliar" onClick={() => setApresentacaoSelecionada(apresentacao)}>
                      Avaliar
                    </button>
                  )}
                  {successId === apresentacao.id && <span className="success-message">✔️ Avaliação enviada!</span>}
                  {errorId === apresentacao.id && <span className="error-message-inline">❌ Já avaliado.</span>}
                </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        );
      })}

      {apresentacaoSelecionada && (
        <AvaliacaoForm
          apresentacao={apresentacaoSelecionada}
          onAvaliacaoSucesso={handleAvaliacaoSucesso}
          onCancel={() => setApresentacaoSelecionada(null)}
          onErroDuplicata={handleErroDuplicata}
        />
      )}
    </div>
  );
}

export default AvaliacaoPremio;
